import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Split, X } from 'lucide-react';

// Same method set used by the plain "Forma de pago" selects across the app:
// each configured Bizum recipient, plus Caixa and Efectivo/Cash.
function methodOptions(bizumRecipients) {
  return (
    <>
      {(bizumRecipients || []).map(r => <SelectItem key={r.id} value={`bizum_${r.id}`}>Bizum {r.name}</SelectItem>)}
      <SelectItem value="caixa">Caixa</SelectItem>
      <SelectItem value="cash">Efectivo/Cash</SelectItem>
    </>
  );
}

const firstMethod = (bizumRecipients) => (bizumRecipients?.length ? `bizum_${bizumRecipients[0].id}` : 'caixa');

// Reusable payment-split control for reservation/rental forms. Sits below the
// existing single "Forma de pago" select; toggling "Dividir pago" swaps to two
// method+amount rows whose sum becomes the form's amount_paid.
export default function PaymentSplitField({ paymentMethod, paymentSplits, amountPaid, bizumRecipients, onChange }) {
  const [splitMode, setSplitMode] = useState((paymentSplits || []).length > 1);

  const enableSplit = () => {
    const first = { method: paymentMethod || 'cash', amount: parseFloat(amountPaid) || 0 };
    const secondMethod = first.method === 'cash' ? 'caixa' : firstMethod(bizumRecipients) === first.method ? 'cash' : firstMethod(bizumRecipients);
    const second = { method: secondMethod, amount: 0 };
    setSplitMode(true);
    onChange({ payment_splits: [first, second], amount_paid: first.amount + second.amount });
  };

  const disableSplit = () => {
    const rows = paymentSplits?.length ? paymentSplits : [{ method: paymentMethod || 'cash', amount: parseFloat(amountPaid) || 0 }];
    setSplitMode(false);
    onChange({ payment_method: rows[0].method, payment_splits: null, amount_paid: rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0) });
  };

  const updateSplit = (idx, field, value) => {
    const rows = (paymentSplits || []).map((r, i) => i === idx ? { ...r, [field]: value } : r);
    const total = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    onChange({ payment_splits: rows, amount_paid: total });
  };

  if (!splitMode) {
    return (
      <Button type="button" variant="ghost" size="sm" onClick={enableSplit} className="h-6 text-xs text-[#4A6D7C] -mt-2" data-testid="split-payment-toggle">
        <Split className="w-3 h-3 mr-1" />Dividir pago en 2 métodos
      </Button>
    );
  }

  const rows = paymentSplits || [];
  const total = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

  return (
    <div className="space-y-2 border border-[#E5E0D8] rounded-md p-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Pago dividido en 2 métodos</Label>
        <Button type="button" variant="ghost" size="icon" onClick={disableSplit} className="h-6 w-6 text-[#E07A5F]" data-testid="split-payment-cancel">
          <X className="w-3 h-3" />
        </Button>
      </div>
      {rows.map((row, i) => (
        <div key={i} className="flex gap-2 items-center">
          <Select value={row.method} onValueChange={v => updateSplit(i, 'method', v)}>
            <SelectTrigger data-testid={`split-method-${i}`} className="border-[#E5E0D8] flex-1"><SelectValue/></SelectTrigger>
            <SelectContent>{methodOptions(bizumRecipients)}</SelectContent>
          </Select>
          <Input type="number" step="0.01" min="0" value={row.amount} onChange={e => updateSplit(i, 'amount', e.target.value)} className="border-[#E5E0D8] w-28" placeholder="€" data-testid={`split-amount-${i}`} />
        </div>
      ))}
      <p className="text-xs text-[#8A9BA8]">Total dividido: <span className="font-semibold text-[#1A2B3C] tabular-nums">{total.toFixed(2)} €</span></p>
    </div>
  );
}
